import { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  IconButton,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import DocumentMetadataModal from './DocumentMetadataModal';
import type { DocumentMetadata } from '@/types/workflow';

interface DocumentMatchingCardProps {
  documentMatches: DocumentMetadata[];
  onDocumentMatchesChange: (matches: DocumentMetadata[]) => void;
}

export default function DocumentMatchingCard({ 
  documentMatches, 
  onDocumentMatchesChange 
}: DocumentMatchingCardProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const handleAddDocument = () => {
    setModalOpen(true);
  };

  const handleSaveDocument = (metadata: DocumentMetadata) => {
    onDocumentMatchesChange([...documentMatches, metadata]);
    setModalOpen(false);
  };

  const handleDeleteDocument = (id: string) => {
    onDocumentMatchesChange(documentMatches.filter(match => match.id !== id));
  };


  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Document Matching
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define document metadata criteria for matching documents to this workflow.
      </Typography>

      {/* Document Match Cards */}
      {documentMatches.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 2,
            '@media (max-width: 768px)': {
              gridTemplateColumns: '1fr'
            }
          }}>
            {documentMatches.map((match) => (
              <Box key={match.id}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    position: 'relative',
                    height: '100%'
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteDocument(match.id)}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      color: 'error.main'
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>

                  <Box sx={{ pr: 4 }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        Pro Forma
                      </Typography>
                      <Chip 
                        label={match.documentTypeLabel} 
                        size="small" 
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>

                    <Box sx={{ mb: 1 }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        Document Type
                      </Typography>
                      <Chip 
                        label={match.categoryLabel} 
                        size="small" 
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>

                    <Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ fontSize: '0.75rem', textTransform: 'uppercase' }}
                      >
                        Territory
                      </Typography>
                      <Chip 
                        label={match.statusLabel} 
                        size="small" 
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Add Document Match Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddDocument}
          sx={{ minWidth: 160 }}
        >
          Add Document Match
        </Button>
      </Box>

      {/* Document Metadata Modal */}
      <DocumentMetadataModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveDocument}
      />
    </Paper>
  );
}
